import React, { FunctionComponent, useRef, useState } from 'react';
import moment from 'moment';
import { v1 as uuid } from 'uuid';
import classNames from 'classnames';
import { Skeleton } from '@material-ui/lab';
import { AutoSizer } from 'react-virtualized';
import gray from '@material-ui/core/colors/grey';
import BackToTopArrow from '@material-ui/icons/ArrowUpward';
import { Box, Fab, Grid, makeStyles, Tooltip } from '@material-ui/core';
import InfiniteScrollView from '../InfiniteScroller';
import { CommentList } from '../../store/modules/application/types';

type CommentInfiniteScrollProps = {
    hasMore: boolean;
    loadMore: Function;
    data: CommentList;
    scrollableTarget: string;
    doShowDetail?: boolean;
};

const useStyles = makeStyles({
    root: {
        '&>.infinite-scroll-component__outerdiv': {
            height: '100%',
        },
    },
    commentUserName: {
        textAlign: 'left',
        paddingRight: 20,
        marginBottom: '1%',
        fontWeight: 600,
        color: gray[800],
        fontSize: 14,
    },
    commentDate: {
        width: '50%',
    },
    commentNodeText: {
        width: '100%',
        wordWrap: 'break-word',
    },
    commentNode: {
        width: '83%',
        justifySelf: 'center',
        marginBottom: '30px',
        fontSize: 14,
        color: gray[600],
        lineHeight: '20px',
        minWidth: 100,
    },
    backToTopWithSideBar: {
        position: 'absolute',
        bottom: 14,
        right: '8vw',
        zIndex: 3,
        backgroundColor: 'white',
    },
    backToTopWithoutSideBar: {
        position: 'absolute',
        bottom: 14,
        right: '9.5vw',
        zIndex: 3,
        backgroundColor: 'white',
    },
    commentSkeleton: {
        width: '80%',
        marginLeft: 'auto',
        marginRight: 'auto',
    },
});
const CommentInfiniteScroll: FunctionComponent<CommentInfiniteScrollProps> = (props) => {
    const ref = useRef<any>(null);

    const { data, hasMore, loadMore, scrollableTarget, doShowDetail } = props;
    const [showBackToTop, setShowBackToTop] = useState<boolean>(false);

    //@ts-ignore
    const classes = useStyles();
    const scrollToTop = () => {
        ref?.current?.scrollTo({ top: 0, behavior: 'smooth' }); //scroll to the top of the table
    };

    const handleScroll = (event: any) => {
        if (event?.target?.scrollTop > 200 && !showBackToTop) {
            setShowBackToTop(true);
        } else if (event?.target?.scrollTop === 0) {
            setShowBackToTop(false);
        }
    };

    return (
        <AutoSizer disableWidth>
            {({ height }) => (
                <Box height={height} className={classes.root}>
                    <InfiniteScrollView
                        data={data}
                        dataHasMore={hasMore}
                        loadMore={loadMore}
                        loader={
                            <div>
                                <Skeleton className={classes.commentSkeleton} />
                                <Skeleton className={classes.commentSkeleton} />
                            </div>
                        }
                        scrollableTarget={scrollableTarget}
                        handleScroll={handleScroll}
                    >
                        <div
                            id={scrollableTarget}
                            style={{
                                height: '100%',
                                overflow: 'auto',
                                width: '80%',
                                margin: 'auto',
                            }}
                            ref={ref}
                        >
                            {data?.length > 0 ? (
                                data.map((comment: any) => {
                                    return (
                                        <Grid key={uuid()} container className={classes.commentNode}>
                                            <Grid container>
                                                <Grid item className={classes.commentUserName}>
                                                    {comment.user?.name}
                                                </Grid>
                                                <Grid item className={classes.commentDate}>
                                                    {moment(comment.timestamp).format('LLL')}
                                                </Grid>
                                            </Grid>
                                            <Grid item className={classes.commentNodeText}>
                                                {comment.text}
                                            </Grid>
                                        </Grid>
                                    );
                                })
                            ) : (
                                <></>
                            )}
                        </div>
                        {showBackToTop && (
                            <Tooltip title="Back to Top" placement="top">
                                <Fab
                                    size="small"
                                    className={classNames({
                                        [classes.backToTopWithoutSideBar]: !doShowDetail,
                                        [classes.backToTopWithSideBar]: doShowDetail,
                                    })}
                                    aria-label="Back to top"
                                    onClick={scrollToTop}
                                >
                                    <BackToTopArrow color="primary" />
                                </Fab>
                            </Tooltip>
                        )}
                    </InfiniteScrollView>
                </Box>
            )}
        </AutoSizer>
    );
};

export default CommentInfiniteScroll;
